import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import type { Work } from '../../types/api';

let cache: Work[] | null = null;
let pending: Promise<Work[]> | null = null;
const when = (w: Work) => (w.completed_date ? Date.parse(w.completed_date) : 0) || 0;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const fetchOnce = async (): Promise<Work[]> =>
  (await apiService.getWorks({ enable: true })).filter((w) => w.cover_image?.url).sort((a, b) => when(b) - when(a));

/** Un échec n'est jamais mémorisé: un second essai part tout de suite, et la demande suivante repart de zéro. */
function load(): Promise<Work[]> {
  if (cache) return Promise.resolve(cache);
  pending ??= (async () => {
    try {
      return (cache = await fetchOnce().catch(async () => { await sleep(900); return fetchOnce(); }));
    } finally {
      pending = null;
    }
  })();
  return pending;
}

/** Réalisations récentes (les plus récentes d'abord), chargées à la demande: `works` est `null` tant qu'elles ne sont pas là. */
export function useRecentWorks(enabled: boolean): { works: Work[] | null; error: boolean; retry: () => void } {
  const [state, setState] = useState<{ works: Work[] | null; error: boolean }>({ works: cache, error: false });

  const run = useCallback(() => {
    setState((s) => ({ ...s, error: false }));
    load().then((works) => setState({ works, error: false })).catch(() => setState({ works: null, error: true }));
  }, []);

  useEffect(() => {
    if (enabled && !state.works && !state.error) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { works: state.works, error: state.error, retry: run };
}
